<template>
  <div>
    <panel class="announcement" v-if="loading">
      <loader :loading="loading"/>
    </panel>
    <panel :title="`${announcement.code} - ${announcement.title}`" :withFooter="false" :withBody="withBody" v-else>
      <div slot="header-right" class="header-right">
        <span class="badge" v-bind:class="announcement.statusSynchronization">{{ $t(`mercadoLivre.migration.statusSynchronization.${announcement.statusSynchronization}.label`) }}</span>
        <span class="badge announcement-status" v-bind:class="announcement.status">{{ $t(`mercadoLivre.migration.${announcement.status}.label`) }}</span>
        <button class="btn btn--danger u-floatRight" v-if="announcement.status == 'pending'" @click="deleteProductMigration">Cancelar migração</button>
      </div>

      <div v-if="details" class="l-row">
        <div class="l-col-2@md vertical-center">
          <img :src="image" :alt="details.title" class="photo" v-if="image">
          <i class="icon icon-image2 no-photo" v-else></i>
        </div>

        <div class="l-col-10@md">
          <div class="l-row">
            <div class="l-col-12@md">
              <form-group label="Módulo" :columns="12">
                <select v-model="moduleSelect" class="form-select" :disabled="details.migration.done">
                  <option :value=null>{{ $t('choose.label') }}</option>
                  <option :value="`${module.type}.${module.identifier}`" v-for="module in modules">
                    {{ $t(`${module.type}.label`) }} {{ $t(`${module.identifier}.label`) }}
                  </option>
                </select>
              </form-group>

              <form-group label="Produto" :columns="12">
                <multiselect
                  v-model="product"
                  :multiple="false"
                  @search-change="searchMethod"
                  :options="products"
                  open-direction="bottom"
                  placeholder="Buscar por título/sku/ean/referencia"
                  label="name"
                  track-by="name"
                  :loading="isLoading"
                  :disabled="!moduleSelect || !details.source.type || details.migration.done"
                  :searchable="true"
                  :internal-search="false"
                  :clear-on-select="true"
                  :close-on-select="true"
                  :show-labels="false"
                  :options-limit="300"
                  :limit="3"
                  :max-height="600"
                  :show-no-results="true"
                  selectLabel="Clique"
                  selectedLabel=""/>
              </form-group>
            </div>
          </div>

          <div class="l-col-12@md" v-if="product">
            {{ details.variationsCombination ? 'Variações' : 'Variação' }}

            <announcement-migration-with-variation
              v-for="announcementVariation in details.variations"
              :key="announcementVariation"
              :announcementVariation="announcementVariation"
              :productVariations="product.variations"
              :migration="details.migration"
              v-if="details.variationsCombination"
              :announcementHasOnlyOneVariation="details.variations.length === 1"
            />

            <announcement-migration-without-variation :announcementVariation="details.variations[0]" :productVariations="product.variations" :migration="details.migration" v-if="!details.variationsCombination"/>

            <button class="btn btn--info u-floatRight" @click="migrateAnnouncement" v-bind:class="{ 'is-loading': isMigrationDoing }" :disabled="details.migration.done && details.variations.length === details.migration.variations.length">{{ $t('migrate.label') }}</button>
          </div>
        </div>
      </div>
    </panel>
  </div>
</template>

<script>
  import { debounce } from 'lodash'
  import api from '@/api'
  import notifier from '@/notifier'
  import Panel from '@/components/template/Panel'
  import Loader from '@/components/template/Loader'
  import FormGroup from '@/components/template/FormGroup'
  import Multiselect from 'vue-multiselect'
  import AnnouncementMigrationWithVariation from '@/components/marketplace/mercadoLivre/migration/WithVariation'
  import AnnouncementMigrationWithoutVariation from '@/components/marketplace/mercadoLivre/migration/WithoutVariation'

  export default {
    data: () => ({
      loading: false,
      show: true,
      moduleSelect: null,
      product: null,
      products: [],
      details: null,
      withBody: false,
      isLoading: false,
      isMigrationDoing: false
    }),
    props: {
      account: {
        type: String,
        required: true
      },
      announcement: {
        type: Object,
        required: true
      },
      modules: {
        type: Array,
        required: true
      }
    },
    mounted () {
      if (this.announcement.status !== 'done') {
        this.loading = true

        api.mercadoLivre.getAnnouncementMigrationDetails(this.announcement.code, this.account)
          .then(item => {
            this.details = item
            this.withBody = true

            if (this.details.source.length) {
              if (this.modules.length === 1) {
                this.details.source = this.modules[0]
              }
            }

            if (this.details.source.type && this.details.source.identifier) {
              this.moduleSelect = `${this.details.source.type}.${this.details.source.identifier}`

              if (this.details.productId) {
                api.getProduct(this.details.source.type, this.details.source.identifier, this.details.productId)
                  .then(product => {
                    this.product = product
                    this.product.name = `${this.product.title} - ${this.product.brand}`
                  })
              }
            }

            if (this.modules.length === 1) {
              this.moduleSelect = `${this.modules[0].type}.${this.modules[0].identifier}`
            }
          })
          .catch(() => (notifier.error('Falha ao buscar os anúncios para migração.', 'Erro')))
          .then(() => (this.loading = false))
      }

      this.search = debounce(this.searchMethod, 3000)
    },
    methods: {
      async searchMethod (search) {
        search = search.replace(/ /g, '+')

        if (this.isLoading || search.length === 0) {
          return false
        }

        this.isLoading = true

        const items = await api.getProductsToMigration(
          this.details.source.type,
          this.details.source.identifier,
          search,
          { limit: 3 }
        )

        this.products = []
        for (let i in items) {
          let item = items[i]
          item.name = `${item.title} - ${item.brand}`

          this.products.push(item)
        }

        this.isLoading = false
      },
      async migrateAnnouncement () {
        this.isMigrationDoing = true

        try {
          const announcement = Object.assign({}, this.details)

          announcement.variations = announcement.variations.filter(variation => variation.sku !== null)

          if (announcement.variations.length === 0) {
            notifier.error('Selecione pelo menos uma variação para migrar este anúncio.', 'Erro')
            this.isMigrationDoing = false
            return false
          }

          await api.mercadoLivre.saveAnnouncementMigration(announcement, this.account)

          for (let i in announcement.variations) {
            const code = announcement.variations[i].code

            if (this.details.migration.variations.indexOf(code) === -1) {
              this.details.migration.variations.push(code)
            }
          }

          this.details.status = 'done'
          this.announcement.status = 'done'
          this.withBody = false

          notifier.success('Anúncio migrado com sucesso.', 'Sucesso')
        } catch (err) {
          notifier.error('Erro ao migrar anúncio.', 'Erro')
        }

        this.isMigrationDoing = false
      },
      async deleteProductMigration () {
        try {
          const announcementCode = this.announcement.code

          await api.mercadoLivre.deleteAnnouncementMigration(announcementCode, this.account)

          notifier.success('Migração cancelada com sucesso.', 'Sucesso')
        } catch (err) {
          notifier.error('Erro ao cancelar migração.', 'Erro')
        }
      }
    },
    computed: {
      image () {
        if (this.loading || this.details === null) {
          return null
        }
        if (this.details.variations[0].images.length) {
          return this.details.variations[0].images[0].url
        }

        return null
      }
    },
    watch: {
      moduleSelect (value) {
        this.product = null

        if (value === null) {
          this.details.source = null
          return false
        }

        const [type, identifier] = value.split('.')
        this.details.source = {
          type: type,
          identifier: identifier
        }
      },
      product (value) {
        if (value === null) {
          this.details.productId = null
          return false
        }

        this.details.productId = value.productId
      }
    },
    components: {
      Panel,
      Loader,
      FormGroup,
      Multiselect,
      AnnouncementMigrationWithVariation,
      AnnouncementMigrationWithoutVariation
    }
  }
</script>

<style lang="sass" scoped>
  .announcement
    min-height: 80px

  .header-right 
    display: flex
    align-items: center

  .announcement-status 
    margin-left: 5px
    margin-right: 5px

  .photo
    max-width: 80%
    margin: 0 auto

  .no-photo
    font-size: 140px
    color: #a9a9a9
    margin: 0 auto

  .vertical-center
    display: flex
    align-items: center
    min-height: 81px

  .partial
    background: #FFA500
    color: #fff
</style>



// WEBPACK FOOTER //
// AnnouncementMigrationCard.vue?39323684